/**
 * MYAC-918 — the person's own conversations in a project: what « Vos
 * conversations » lists, with one state per row, and the person's own marks on
 * them (seen, pinned, following).
 *
 * Reads (client-data-contract): the conversation ROWS
 * `projects/{pid}/views/chat_sessions/rows` where `owner_uid == me`, newest
 * first — no per-person copy of the rows (design decision D7, 2026-10-05). The
 * person's own state lives in `users/{uid}/conversation_state/{chat_id}`
 * (`seen_at`, `following`, `pinned`): a tier-1 collection the browser writes
 * DIRECTLY under an owner-only rule, no command — a « seen » command would fire
 * Eventarc on every open (D8).
 *
 * Writes that the backend must apply (a rename) are commands on the
 * `conversation` queue (`useConversationCommands`), resolved at admission.
 *
 * States, from the row against `seen_at` (Arslene, 2026-10-12):
 *   working   `run_status == running` (bounded like the chat does)
 *   proposal  `pending_approval` — strict: cleared only by a decision
 *   answer    `last_activity_at > seen_at` — stamped only by a turn that ended done
 *
 * MYAC-1269 — sharing. Two more reads on the same rows, each provable by the
 * rule's own branch: « Partagées avec vous » (`participant_uids array-contains
 * me`) and « Partagées dans le projet » (`visibility == "project"`), both in
 * the bound project. The share commands (`share`, `set_right`, `set_visibility`,
 * `remove`, `leave`, `stop`) go through the same queue as a rename and are
 * followed to their verdict; a refusal is said with its reason.
 */
import { defineStore } from 'pinia'
import { collection, doc, onSnapshot, query, serverTimestamp, setDoc, where } from 'firebase/firestore'
import type { Unsubscribe } from 'firebase/firestore'
import { useFirestore } from 'vuefire'
import i18n from '@/plugins/i18n'
import { viewDocPath } from '@/composables/useClientViews'
import {
  conversationReasonI18nKey, leaveConversationCommand, removeParticipantsCommand, renameCommand,
  setRightCommand, setVisibilityCommand, shareCommand, stopSharingCommand, watchCommand,
  type ShareRight, type ShareVisibility,
} from '@/composables/useConversationCommands'
import {
  conversationRightOf, timeMillis, turnRowIsRunning,
  type ChatSessionRunRow, type ConversationRight,
} from '@/stores/chatbot'
import { useAppStore } from '@/stores/app'
import { useUsersStore } from '@/stores/users'
import { logger } from '@/utils/logger'

export const CHAT_SESSIONS_VIEW = 'chat_sessions'
export const CONVERSATION_STATE_COLLECTION = 'conversation_state'
/** The newest conversations a list shows; the rest are read but not listed. */
export const LIST_LIMIT = 50
/** MYAC-1269 — the most people one conversation names (the backend's cap, `_apply_sharing`). */
export const MAX_SHARE_PARTICIPANTS = 50
/** How long a conversation command may stay without a verdict before the dialog gives up. */
export const COMMAND_VERDICT_TIMEOUT_MS = 30_000

/** Where the conversation's state stands, as the lists say it — at most one. */
export type ConversationState = 'working' | 'proposal' | 'answer' | null

/** One conversation of the person, as the list reads it (the row, typed). */
export interface ConversationRow {
  /** The chat id (the ADK session id, the row id). */
  id: string
  projectId: string
  title: string
  /** `updated_at` of the row, in ms — the list's order. */
  updatedAt: number
  /** The end of the last turn that produced an answer, in ms. */
  lastActivityAt: number | null
  /** The row's run fields, read against the clock: a turn older than the
   *  dispatch deadline is dead whatever the row says (same bound as the chat). */
  runStatus: ChatSessionRunRow['run_status']
  runStartedAt: number | null
  pendingApproval: boolean
  titleEdited: boolean
  deleted: boolean
  messageCount: number
  /** MYAC-1269 — who started it, and the sharing projection (absent = private). */
  ownerUid: string
  visibility: 'private' | 'participants' | 'project'
  projectRight: 'read' | 'write'
  participantUids: string[]
  writerUids: string[]
  /** Who sent the turn that stamped `lastActivityAt` ("" before MYAC-1269). */
  lastActivityBy: string
}

/** The person's own marks on a conversation. */
export interface ConversationPersonalState {
  seenAt: number | null
  following: boolean
  pinned: boolean
}

/** « J'y travaille » only while the turn can still be alive (review PR 929). */
export function isWorking(row: ConversationRow, now = Date.now()): boolean {
  return turnRowIsRunning({ run_status: row.runStatus, run_started_at: row.runStartedAt }, now)
}

export function conversationState(
  row: ConversationRow, personal: ConversationPersonalState | undefined, now = Date.now(),
  /** A conversation never opened counts its last answer as new (false for the
   *  project-wide ones nobody named me on, MYAC-1269: they are not waiting for me). */
  unseenIsNew = true,
): ConversationState {
  if (isWorking(row, now)) return 'working'
  if (row.pendingApproval) return 'proposal'
  if (personal?.seenAt == null && !unseenIsNew) return null
  if (row.lastActivityAt !== null && row.lastActivityAt > (personal?.seenAt ?? 0)) return 'answer'
  return null
}

/** The row document as the list reads it. */
export function rowFromDoc(id: string, projectId: string, data: ChatSessionRunRow & Record<string, unknown>): ConversationRow {
  return {
    id,
    projectId,
    title: typeof data.title === 'string' ? data.title : '',
    updatedAt: timeMillis(data.updated_at) ?? 0,
    lastActivityAt: timeMillis(data.last_activity_at),
    runStatus: data.run_status,
    runStartedAt: timeMillis(data.run_started_at),
    pendingApproval: data.pending_approval === true,
    titleEdited: data.title_edited === true,
    deleted: data.deleted === true,
    messageCount: typeof data.message_count === 'number' ? data.message_count : 0,
    ownerUid: typeof data.owner_uid === 'string' ? data.owner_uid : '',
    visibility: data.visibility === 'project' || data.visibility === 'participants' ? data.visibility : 'private',
    projectRight: data.project_right === 'write' ? 'write' : 'read',
    participantUids: Array.isArray(data.participant_uids) ? data.participant_uids.filter((u): u is string => typeof u === 'string') : [],
    writerUids: Array.isArray(data.writer_uids) ? data.writer_uids.filter((u): u is string => typeof u === 'string') : [],
    lastActivityBy: typeof data.last_activity_by === 'string' ? data.last_activity_by : '',
  }
}

/** MYAC-1269 — which half of the list is shown: the person's own, or what others shared. */
export type ConversationListTab = 'mine' | 'shared'

/** MYAC-1269 — the project-wide share, as the dialog offers it. */
export type ProjectShareMode = 'off' | 'read' | 'write'
/** MYAC-1269 — a sharing as chosen in the dialog, or as the row says it. */
export interface SharingChoice {
  participants: Record<string, ShareRight>
  project: ProjectShareMode
}

/**
 * The commands that take a sharing from `current` to `wanted` (the dialog's
 * one gesture, `applySharing`). The product rules live here, in one place the
 * dialog's « changed » and the store's sender both read:
 *  - only a person the owner UNTICKED is removed — never one the dialog could not
 *    list (members not loaded yet, a name the roster lacks): `known` says who the
 *    dialog could show, and absence from `wanted` means nothing for the others;
 *  - each right's group is (re)named in one `share`;
 *  - the project-wide mode is set FIRST when it narrows (everyone loses a right
 *    the owner just took away before anything else can fail), LAST when it widens.
 */
export interface SharingDelta {
  removed: string[]
  shares: Array<{ right: ShareRight; uids: string[] }>
  visibility: { visibility: ShareVisibility; projectRight: ShareRight } | null
  /** The project-wide change narrows what people may do: it goes first. */
  narrows: boolean
}
const PROJECT_MODE_RANK: Record<ProjectShareMode, number> = { off: 0, read: 1, write: 2 }
export function sharingDelta(current: SharingChoice, wanted: SharingChoice, known: Iterable<string>): SharingDelta {
  const listed = new Set(known)
  const removed = Object.keys(current.participants).filter((uid) => listed.has(uid) && !(uid in wanted.participants))
  const shares = (['write', 'read'] as const)
    .map((right) => ({
      right,
      uids: Object.entries(wanted.participants)
        .filter(([uid, r]) => r === right && current.participants[uid] !== right)
        .map(([uid]) => uid),
    }))
    .filter((group) => group.uids.length > 0)
  const visibility = wanted.project === current.project ? null : {
    visibility: (wanted.project === 'off' ? 'participants' : 'project') as ShareVisibility,
    projectRight: (wanted.project === 'write' ? 'write' : 'read') as ShareRight,
  }
  return {
    removed, shares, visibility,
    narrows: visibility !== null && PROJECT_MODE_RANK[wanted.project] < PROJECT_MODE_RANK[current.project],
  }
}
/** Something to send: the dialog's « Enregistrer » is live exactly then. */
export function sharingChanged(delta: SharingDelta): boolean {
  return delta.removed.length > 0 || delta.shares.length > 0 || delta.visibility !== null
}

/** The person's right on a listed conversation (the backend's rule, `conversationRightOf`). */
export function rowRight(row: ConversationRow, uid: string): ConversationRight {
  return conversationRightOf({
    owner_uid: row.ownerUid, visibility: row.visibility, project_right: row.projectRight,
    participant_uids: row.participantUids, writer_uids: row.writerUids,
  }, uid)
}

export const conversationsPath = (projectId: string) => `${viewDocPath(projectId, CHAT_SESSIONS_VIEW)}/rows`
/** How often the states are re-read against the clock while bound. */
export const CLOCK_TICK_MS = 30_000
export const personalStatePath = (uid: string) => `users/${uid}/${CONVERSATION_STATE_COLLECTION}`

/**
 * The signed-in person's uid, or null outside a session (tests, sign-out).
 * Reads the state, never the `uid` getter: that getter THROWS when nobody is
 * signed in, and a getter here that touched it would make Vue record it as a
 * dependency and re-run it on a later dirty check, outside any try/catch
 * (three unhandled errors in the chat panel's tests, PR 976 CI).
 */
function uidOrNull(): string | null {
  try { return useUsersStore().user?.uid ?? null } catch { return null }
}

export const useConversationsStore = defineStore('conversations', {
  state: () => ({
    projectId: null as string | null,
    rows: [] as ConversationRow[],
    /** MYAC-1269 — the rows naming me as a participant, and the project-wide ones. */
    sharedRows: [] as ConversationRow[],
    projectRows: [] as ConversationRow[],
    personal: {} as Record<string, ConversationPersonalState>,
    /** A successful read happened: an empty list means « none yet », not « unknown ». */
    loaded: false,
    error: false,
    /** The two shared reads, each with its own failure flag (a success of one
     *  must never hide the other's failure as « nothing shared »). */
    sharedReadFailed: { named: false, project: false } as Record<'named' | 'project', boolean>,
    renaming: {} as Record<string, boolean>,
    /** A share command in flight on a conversation (the dialog waits for its verdict). */
    sharing: {} as Record<string, boolean>,
    /** MYAC-1269 — the half of the list on screen; a filter, so it survives a route change. */
    listTab: 'mine' as ConversationListTab,
    /** MYAC-1269 — « Partager… » asked from a list row, for this conversation. */
    shareRequest: null as { chatId: string; at: number } | null,
    /** Period groups the person folded in the list, `<tab>:<period>` (shared by the page column and the panel). */
    foldedGroups: {} as Record<string, boolean>,
    unsubscribeRows: null as Unsubscribe | null,
    unsubscribeShared: null as Unsubscribe | null,
    unsubscribeProject: null as Unsubscribe | null,
    unsubscribePersonal: null as Unsubscribe | null,
    /** Ticks while bound, so « J'y travaille » ends when a dead turn's deadline passes. */
    clock: Date.now(),
    clockTimer: null as ReturnType<typeof setInterval> | null,
  }),
  getters: {
    /** Mine in this project, newest first, deleted ones out. */
    recent: (state): ConversationRow[] => state.rows.filter((r) => !r.deleted),
    /** MYAC-1269 — others' conversations that name me, newest first (never my own). */
    sharedWithMe(state): ConversationRow[] {
      const uid = uidOrNull()
      return state.sharedRows.filter((r) => !r.deleted && r.ownerUid !== uid)
    },
    /** MYAC-1269 — the project-wide ones that are neither mine nor already named to me. */
    sharedInProject(state): ConversationRow[] {
      const uid = uidOrNull()
      const named = new Set(this.sharedWithMe.map((r) => r.id))
      return state.projectRows.filter((r) => !r.deleted && r.ownerUid !== uid && !named.has(r.id))
    },
    /** Everything listed — the handle's signals read these. */
    listed(): ConversationRow[] {
      return [...this.recent, ...this.sharedWithMe, ...this.sharedInProject]
    },
    /** What others shared, both headings together. */
    shared(): ConversationRow[] {
      return [...this.sharedWithMe, ...this.sharedInProject]
    },
    /** Something waits in a shared conversation (the « Partagées » switch carries a dot). */
    sharedHasNews(): boolean {
      return this.shared.some((r) => this.stateOf(r) !== null)
    },
    /** One of the two shared reads failed: said under the heading, never shown as « none ». */
    sharedError: (state): boolean => state.sharedReadFailed.named || state.sharedReadFailed.project,
    stateOf: (state) => (row: ConversationRow): ConversationState => {
      // MYAC-1269 — a project-wide conversation nobody named me on, never opened:
      // nothing in it waits for me, so no « Nouvelle réponse » and no dot forever.
      const uid = uidOrNull()
      const forMe = row.ownerUid === uid || (uid !== null && row.participantUids.includes(uid))
      return conversationState(row, state.personal[row.id], state.clock, forMe)
    },
    /** « Marc a écrit »: who sent the turn behind the new answer, when it was not me. */
    wroteBy() {
      const uid = uidOrNull()
      return (row: ConversationRow): string | null =>
        row.lastActivityBy && row.lastActivityBy !== uid ? row.lastActivityBy : null
    },
    /** What I may do in a listed conversation. */
    rightOf() {
      return (row: ConversationRow): ConversationRight => rowRight(row, uidOrNull() ?? '')
    },
  },
  actions: {
    uidOrNull(): string | null {
      return uidOrNull()
    },

    /** Follow this project's conversations of mine and my personal marks. Idempotent per project. */
    bind(projectId: string | null): void {
      if (projectId === this.projectId && this.unsubscribeRows) return
      this.unbind()
      this.projectId = projectId
      const uid = this.uidOrNull()
      if (!projectId || !uid) return
      const db = useFirestore()
      this.clock = Date.now()
      this.clockTimer = setInterval(() => { this.clock = Date.now() }, CLOCK_TICK_MS)
      // One equality filter, sorted here: the person's own conversations in a
      // project are tens, and an `orderBy` beside the `where` would need a
      // composite index on every environment for nothing.
      this.unsubscribeRows = onSnapshot(
        query(collection(db, conversationsPath(projectId)), where('owner_uid', '==', uid)),
        (snap) => {
          this.clock = Date.now()
          this.rows = snap.docs
            .map((d) => rowFromDoc(d.id, projectId, d.data() as ChatSessionRunRow & Record<string, unknown>))
            .sort((a, b) => b.updatedAt - a.updatedAt)
            .slice(0, LIST_LIMIT)
          this.loaded = true
          this.error = false
        },
        (err) => {
          logger.error('conversations: rows subscription failed', err)
          this.error = true
        },
      )
      // MYAC-1269 — the two shared lists. Each query is exactly one branch of the
      // row's read rule (`uid in participant_uids`, `visibility == "project"`),
      // so Firestore can prove it; no composite index, sorted here like mine.
      const sharedList = (field: 'sharedRows' | 'projectRows', read: 'named' | 'project') =>
        (snap: { docs: Array<{ id: string; data: () => unknown }> }) => {
          this.clock = Date.now()
          this[field] = snap.docs
            .map((d) => rowFromDoc(d.id, projectId, d.data() as ChatSessionRunRow & Record<string, unknown>))
            .sort((a, b) => b.updatedAt - a.updatedAt)
            .slice(0, LIST_LIMIT)
          this.sharedReadFailed = { ...this.sharedReadFailed, [read]: false }
        }
      const sharedFailed = (what: string, read: 'named' | 'project') => (err: unknown) => {
        logger.error(`conversations: ${what} subscription failed`, err)
        this.sharedReadFailed = { ...this.sharedReadFailed, [read]: true }
      }
      this.unsubscribeShared = onSnapshot(
        query(collection(db, conversationsPath(projectId)), where('participant_uids', 'array-contains', uid)),
        sharedList('sharedRows', 'named'), sharedFailed('shared-with-me rows', 'named'),
      )
      this.unsubscribeProject = onSnapshot(
        query(collection(db, conversationsPath(projectId)), where('visibility', '==', 'project')),
        sharedList('projectRows', 'project'), sharedFailed('project-wide rows', 'project'),
      )
      this.unsubscribePersonal = onSnapshot(
        collection(db, personalStatePath(uid)),
        (snap) => {
          const next: Record<string, ConversationPersonalState> = {}
          for (const d of snap.docs) {
            // `estimate`: right after markSeen the local write still waits for the
            // server time; read as null it flashed « Nouvelle réponse » on the row
            // just opened for half a second (MYAC-1269).
            const data = d.data({ serverTimestamps: 'estimate' }) as { seen_at?: unknown; following?: unknown; pinned?: unknown }
            next[d.id] = {
              seenAt: timeMillis(data.seen_at),
              following: data.following === true,
              pinned: data.pinned === true,
            }
          }
          this.personal = next
        },
        (err) => logger.error('conversations: personal state subscription failed', err),
      )
    },

    unbind(): void {
      this.unsubscribeRows?.()
      this.unsubscribeShared?.()
      this.unsubscribeProject?.()
      this.unsubscribePersonal?.()
      this.unsubscribeRows = null
      this.unsubscribeShared = null
      this.unsubscribeProject = null
      this.unsubscribePersonal = null
      if (this.clockTimer) clearInterval(this.clockTimer)
      this.clockTimer = null
      this.rows = []
      this.sharedRows = []
      this.projectRows = []
      this.loaded = false
      this.error = false
      this.sharedReadFailed = { named: false, project: false }
      this.projectId = null
    },

    /** MYAC-1269 — fold or unfold a period of the list (shared by the page column and the panel). */
    toggleGroup(key: string): void {
      this.foldedGroups = { ...this.foldedGroups, [key]: !this.foldedGroups[key] }
    },

    /** MYAC-1269 — « Partager… » from a list row: the open conversation's share button answers it. */
    requestShare(chatId: string): void {
      this.shareRequest = { chatId, at: Date.now() }
    },

    setListTab(tab: ConversationListTab): void {
      this.listTab = tab
    },

    /** The person has this conversation in view now (direct write, owner-only rule). */
    async markSeen(chatId: string): Promise<void> {
      const uid = this.uidOrNull()
      if (!uid || !chatId) return
      try {
        await setDoc(doc(useFirestore(), `${personalStatePath(uid)}/${chatId}`),
          { seen_at: serverTimestamp() }, { merge: true })
      } catch (err) {
        logger.error('conversations: seen_at write failed', err)
      }
    },

    async setPinned(chatId: string, pinned: boolean): Promise<void> {
      await this.setPersonalFlag(chatId, { pinned })
    },

    async setFollowing(chatId: string, following: boolean): Promise<void> {
      await this.setPersonalFlag(chatId, { following })
    },

    async setPersonalFlag(chatId: string, flags: { pinned?: boolean; following?: boolean }): Promise<void> {
      const uid = this.uidOrNull()
      if (!uid || !chatId) return
      try {
        await setDoc(doc(useFirestore(), `${personalStatePath(uid)}/${chatId}`), flags, { merge: true })
      } catch (err) {
        logger.error('conversations: personal flag write failed', err)
      }
    },

    /**
     * Rename a conversation: a `rename` command the backend applies at admission
     * (owner only; refused while an answer is being written). The row carries the
     * new title within a second; a refusal is said with its reason.
     */
    async rename(projectId: string, chatId: string, title: string): Promise<void> {
      const clean = title.replace(/\s+/g, ' ').trim()
      if (!projectId || !chatId || !clean) return
      this.renaming = { ...this.renaming, [chatId]: true }
      try {
        await this.followToVerdict(projectId, () => renameCommand(projectId, { chat_id: chatId, title: clean }),
          'conversations.rename_refused')
      } finally {
        const { [chatId]: _, ...rest } = this.renaming
        void _
        this.renaming = rest
      }
    },

    /**
     * Write one command and wait for the backend's verdict: resolves `true` on
     * `done`, `false` on a refusal (said with its reason) or a failure. The one
     * path every inline conversation command takes (rename, the share commands).
     */
    async followToVerdict(projectId: string, deposit: () => Promise<string>, refusedTitleKey: string): Promise<boolean> {
      try {
        const commandId = await deposit()
        return await new Promise<boolean>((resolve) => {
          const say = (detailKey: string, severity: 'warn' | 'error') =>
            useAppStore().toast(i18n.global.t(refusedTitleKey), i18n.global.t(detailKey), severity)
          let stop: () => void = () => {}
          const settle = (ok: boolean) => {
            clearTimeout(timer)
            stop()
            resolve(ok)
          }
          // No verdict in time (the admitter down, a listener that never fires): the
          // dialog must not wait forever, and a silent close would read as « done ».
          const timer = setTimeout(() => {
            say('conversations.command_no_answer', 'warn')
            settle(false)
          }, COMMAND_VERDICT_TIMEOUT_MS)
          stop = watchCommand(projectId, commandId, (state) => {
            if (state.status === 'rejected') {
              say(conversationReasonI18nKey(state.reasonKey), 'warn')
              settle(false)
            } else if (state.status === 'done') {
              settle(true)
            } else if (state.status === 'failed') {
              say('chat.command.reason.internal', 'error')
              settle(false)
            } else if (state.status === 'cancelled') {
              say('conversations.command_cancelled', 'warn')
              settle(false)
            }
          }, { onError: () => { say('chat.command.reason.internal', 'error'); settle(false) } })
        })
      } catch (err) {
        logger.error('conversations: command failed', err)
        useAppStore().toast(i18n.global.t(refusedTitleKey), i18n.global.t('chat.command.reason.internal'), 'error')
        return false
      }
    },

    // ── MYAC-1269 — the share commands (owner only, `leave` for a participant) ──

    async runShare(chatId: string, run: () => Promise<boolean>): Promise<boolean> {
      this.sharing = { ...this.sharing, [chatId]: true }
      try {
        return await run()
      } finally {
        const { [chatId]: _, ...rest } = this.sharing
        void _
        this.sharing = rest
      }
    },

    /** Name participants with one right; a uid already named takes the new right. */
    share(projectId: string, chatId: string, uids: string[], right: ShareRight): Promise<boolean> {
      if (!projectId || !chatId || !uids.length) return Promise.resolve(false)
      return this.runShare(chatId, () => this.followToVerdict(projectId,
        () => shareCommand(projectId, { chat_id: chatId, uids, right }), 'conversations.share.refused'))
    },

    setRight(projectId: string, chatId: string, uid: string, right: ShareRight): Promise<boolean> {
      if (!projectId || !chatId || !uid) return Promise.resolve(false)
      return this.runShare(chatId, () => this.followToVerdict(projectId,
        () => setRightCommand(projectId, { chat_id: chatId, uid, right }), 'conversations.share.refused'))
    },

    /** Share with the whole project (`project`, read-only or edit) or stop doing so (`participants`). */
    setVisibility(projectId: string, chatId: string, visibility: ShareVisibility, projectRight: ShareRight = 'read'): Promise<boolean> {
      if (!projectId || !chatId) return Promise.resolve(false)
      return this.runShare(chatId, () => this.followToVerdict(projectId,
        () => setVisibilityCommand(projectId, { chat_id: chatId, visibility, project_right: projectRight }),
        'conversations.share.refused'))
    },

    // csm-event: none — a sharing change is not an engagement signal (MYAC-1269)
    removeParticipants(projectId: string, chatId: string, uids: string[]): Promise<boolean> {
      if (!projectId || !chatId || !uids.length) return Promise.resolve(false)
      return this.runShare(chatId, () => this.followToVerdict(projectId,
        () => removeParticipantsCommand(projectId, { chat_id: chatId, uids }), 'conversations.share.refused'))
    },

    /** Leave a conversation shared with me (a named participant only). */
    leave(projectId: string, chatId: string): Promise<boolean> {
      if (!projectId || !chatId) return Promise.resolve(false)
      return this.runShare(chatId, () => this.followToVerdict(projectId,
        () => leaveConversationCommand(projectId, chatId), 'conversations.leave_refused'))
    },

    /**
     * The share dialog's one gesture: make the conversation's sharing match
     * `wanted`, from `current` (what the row says), for the people the dialog
     * could list (`known`). The product rules live in `sharingDelta`: a narrowing
     * project-wide mode first, then the unticked people are removed (so nobody
     * keeps a right the owner just took away), then each right's group is
     * (re)named in one `share`, then a widening project-wide mode. Stops at the
     * first refusal.
     */
    async applySharing(
      projectId: string, chatId: string, current: SharingChoice, wanted: SharingChoice,
      known: Iterable<string> = Object.keys(current.participants),
    ): Promise<boolean> {
      return this.runShare(chatId, async () => {
        const delta = sharingDelta(current, wanted, known)
        const title = 'conversations.share.refused'
        const setMode = () => delta.visibility === null ? Promise.resolve(true) : this.followToVerdict(projectId,
          () => setVisibilityCommand(projectId, {
            chat_id: chatId, visibility: delta.visibility!.visibility, project_right: delta.visibility!.projectRight,
          }), title)
        if (delta.narrows && !await setMode()) return false
        if (delta.removed.length && !await this.followToVerdict(projectId,
          () => removeParticipantsCommand(projectId, { chat_id: chatId, uids: delta.removed }), title)) {
          return false
        }
        for (const { right, uids } of delta.shares) {
          if (!await this.followToVerdict(projectId, () => shareCommand(projectId, { chat_id: chatId, uids, right }), title)) {
            return false
          }
        }
        if (!delta.narrows && !await setMode()) return false
        return true
      })
    },

    /** Stop every share at once: the others lose it, history included. */
    // csm-event: none — a sharing change is not an engagement signal (MYAC-1269)
    stopSharing(projectId: string, chatId: string): Promise<boolean> {
      if (!projectId || !chatId) return Promise.resolve(false)
      return this.runShare(chatId, () => this.followToVerdict(projectId,
        () => stopSharingCommand(projectId, chatId), 'conversations.share.refused'))
    },
  },
})
